"use client";
import { SelectSVG } from "@/components/ui/SVG";
import { DocumentInput, ProductClientState } from "@/zod/product.schema";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

type EditDocsPanelProps = {
  isOpen: boolean;
  onDocumentPick?: (fileSlug: string, fileTitle: string) => void;
  onDocumentUpload?: (
    file: File,
    fileSlug: string,
    fileTitle: string
  ) => Promise<void>;
};

type DocsStructure = {
  sections: {
    title: string;
    docs: { title: string; slug: string; viewable?: boolean }[];
  }[];
  fileTypes: string[];
};

function EditDocsPanel({
  isOpen,
  onDocumentPick,
  onDocumentUpload,
}: EditDocsPanelProps) {
  const { getValues, setValue, watch } = useFormContext<ProductClientState>();
  const [docsStructure, setDocsStructure] = useState<DocsStructure | null>(
    null
  );
  const [panelError, setPanelError] = useState<string | null>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStructure() {
      try {
        const res = await fetch("/api/docs-panel-structure");
        if (!res.ok) throw new Error("Failed to fetch structure");
        const json = await res.json();
        setDocsStructure(json);
      } catch (error: any) {
        setPanelError(
          error?.message ||
            "Failed to load document structure. Please try again."
        );
        console.error("Error fetching docs structure:", error);
      }
    }
    fetchStructure();
  }, []);

  function updateDocumentField(
    fileSlug: string,
    field: keyof DocumentInput,
    value: string | number
  ) {
    const docs = getValues("documents") || [];

    const docExists = docs.some((doc) => doc.file_slug === fileSlug);

    let newDocuments;

    if (docExists) {
      newDocuments = docs.map((doc) =>
        doc.file_slug === fileSlug ? { ...doc, [field]: value } : doc
      );
    } else {
      const newDoc: DocumentInput = {
        file_slug: fileSlug,
        file_title: "", // maybe set this to slug or empty initially
        url: "",
        file_date: "",
        file_lang: "EN",
        file_size: 0,
        file_type: docsStructure?.fileTypes[0] || "",
        [field]: value,
      };
      newDocuments = [...docs, newDoc];
    }

    setValue("documents", newDocuments, { shouldValidate: true });
  }

  async function handleFileUpload(
    e: React.ChangeEvent<HTMLInputElement>,
    fileSlug: string,
    fileTitle: string
  ) {
    if (!onDocumentUpload) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingKey(fileSlug);
    try {
      await onDocumentUpload(file, fileSlug, fileTitle);
    } catch (error: any) {
      console.error("Document upload failed:", error);
    } finally {
      setUploadingKey(null);
      e.target.value = "";
    }
  }

  if (!docsStructure) {
    return (
      <div className="p-4 text-center text-gray-500">
        Loading document structure...
      </div>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          transition={{ duration: 0.2 }}
          style={{ overflow: "hidden" }}
          className="flex flex-col bg-grayCustom lg:bg-transparent"
        >
          {panelError && (
            <div
              className="mx-4 my-2 rounded border border-red-300 bg-red-50 text-red-700 p-2 text-xs"
              role="alert"
            >
              {panelError}
            </div>
          )}
          {docsStructure.sections.map((section) => (
            <div
              key={section.title}
              className="flex flex-col my-[2px] mx-[10px] rounded-sm border border-gray-200 bg-white"
            >
              <div className="flex flex-col pl-8 divide-y divide-gray-200">
                {section.docs.map((doc) => {
                  const fileSlug = doc.slug;
                  const fileTitle = doc.title;
                  const fileData = watch("documents")?.find(
                    (f) => f.file_slug === fileSlug
                  );
                  const isUploading = uploadingKey === fileSlug;
                  const canOpenInline =
                    (doc.viewable ?? true) && Boolean(fileData?.url);

                  return (
                    <div
                      key={fileSlug}
                      className="flex justify-between w-full py-3 relative"
                    >
                      {isUploading && (
                        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center text-slateCustom z-10 rounded-md">
                          Uploading...
                        </div>
                      )}
                      <div className="flex flex-col flex-grow">
                        {canOpenInline ? (
                          <a
                            href={fileData?.url || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-fit mb-4 text-slateCustom hover:text-slateCustom"
                          >
                            <h3 className="text-lg font-semibold">
                              {fileTitle}
                            </h3>
                          </a>
                        ) : (
                          <h3 className="w-fit mb-4 text-lg font-semibold text-gray-500">
                            {fileTitle}
                          </h3>
                        )}
                        <div className="grid grid-cols-12 justify-between gap-x-1 text-xs pr-4">
                          <input
                            type="date"
                            className="border border-gray-300 rounded text-xs col-span-4 focus:outline-none focus:border-slateCustom p-1"
                            value={
                              fileData?.file_date
                                ? fileData.file_date.split("T")[0]
                                : ""
                            }
                            onChange={(e) =>
                              updateDocumentField(
                                fileSlug,
                                "file_date",
                                e.target.value
                                  ? new Date(e.target.value).toISOString()
                                  : ""
                              )
                            }
                          />

                          <select
                            className="border border-gray-300 rounded p-1 text-xs col-span-3 focus:outline-none focus:border-slateCustom"
                            value={fileData?.file_type ?? ""}
                            onChange={(e) =>
                              updateDocumentField(
                                fileSlug,
                                "file_type",
                                e.target.value
                              )
                            }
                          >
                            <option value="" disabled>
                              Type
                            </option>
                            {docsStructure.fileTypes.map((type) => (
                              <option key={type} value={type}>
                                {type.toUpperCase()}
                              </option>
                            ))}
                          </select>

                          <div className="flex border justify-between items-center border-gray-300 rounded text-xs col-span-3 hover:border-slateCustom overflow-hidden">
                            <input
                              type="number"
                              className="w-4/6 p-1 focus:outline-none"
                              value={fileData?.file_size || ""}
                              placeholder="Size"
                              onChange={(e) =>
                                updateDocumentField(
                                  fileSlug,
                                  "file_size",
                                  parseInt(e.target.value, 10) || 0
                                )
                              }
                            />
                            <span className="leading-7 px-1 bg-gray-200 h-full w-2/6 text-center">
                              KB
                            </span>
                          </div>

                          <select
                            className="border border-gray-300 rounded p-1 text-xs col-span-2 focus:outline-none focus:border-slateCustom"
                            value={fileData?.file_lang ?? "EN"}
                            onChange={(e) =>
                              updateDocumentField(
                                fileSlug,
                                "file_lang",
                                e.target.value
                              )
                            }
                          >
                            <option value="EN">EN</option>
                            <option value="FA">FA</option>
                          </select>
                        </div>
                      </div>
                      {onDocumentPick ? (
                        <button
                          type="button"
                          className="h-fit mr-4 px-3 text-xs rounded cursor-pointer"
                          onClick={() => onDocumentPick(fileSlug, fileTitle)}
                        >
                          <SelectSVG className="w-10 p-2" svgColor="slateCustom" accentColor="slateCustom" />
                        </button>
                      ) : onDocumentUpload ? (
                        <>
                          <label
                            htmlFor={`upload-${fileSlug}`}
                            className="h-fit mr-4 px-3 text-xs rounded cursor-pointer"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-10 p-2"
                              viewBox="0 0 9.56 8.39"
                            >
                              <path
                                className="fill-slateCustom"
                                d="M8.26,2.92c-.07-1.62-1.42-2.92-3.05-2.92-.9,0-1.78,.42-2.35,1.12-.1-.02-.21-.03-.31-.03-.94,0-1.7,.76-1.7,1.7,0,.12,.01,.23,.04,.35C.26,3.57-.08,4.31,.01,5.07c.12,1.02,1.02,1.79,2.08,1.79h1.76c.15,0,.26-.12,.26-.27s-.12-.26-.26-.26h-1.77c-.71,0-1.32-.45-1.5-1.1-.19-.69,.12-1.4,.73-1.73l.2-.11-.07-.21c-.04-.13-.07-.26-.07-.38,0-.65,.53-1.17,1.17-1.17,.11,0,.23,.02,.34,.05l.18,.05,.11-.15c.47-.65,1.23-1.04,2.04-1.04,1.39,0,2.52,1.13,2.52,2.54,0,.02,0,.03,0,.04v.21s.15,.04,.15,.04c.61,.17,1.05,.65,1.13,1.24,.06,.44-.06,.87-.35,1.2-.29,.33-.7,.52-1.14,.52h-1.82c-.15,0-.26,.12-.26,.26s.12,.27,.26,.27h1.76c1.02,0,1.88-.7,2.06-1.67,.17-.96-.37-1.91-1.27-2.26Z"
                              />
                              <path
                                className="fill-slateCustom"
                                d="M5.72,5.01c.08,0,.14-.02,.19-.07,.11-.1,.11-.27,.01-.37l-1.14-1.23-1.14,1.23c-.1,.11-.09,.27,.01,.37,.11,.1,.27,.09,.37-.01l.49-.52v3.73c0,.15,.12,.26,.26,.26s.26-.12,.26-.26v-3.73l.49,.52c.05,.05,.11,.08,.18,.08Z"
                              />
                            </svg>
                          </label>
                          <input
                            id={`upload-${fileSlug}`}
                            type="file"
                            className="hidden"
                            disabled={isUploading}
                            onChange={(e) =>
                              handleFileUpload(e, fileSlug, fileTitle)
                            }
                          />
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default EditDocsPanel;
